import React, { useEffect, useCallback, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/redux/Store';
import {
  fetchSpotlightFeed,
  fetchLocationFeed,
  fetchKlyqueFeed,
  loadMoreFeed,
  fetchKlyque,
} from '@/redux/thunks/dashboardThunks';
import {
  selectActiveFeedTab,
  selectActiveFeed,
  selectFeedHasMore,
  selectFeedLoading,
} from '@/redux/selectors/dashboardSelectors';
import { setActiveFeedTab, resetFeedOffset, setLocationRadius, setLocationUnit } from '@/redux/slices/dashboardSlice';
import { LoadingSpinner } from '@/components';
import { PostCard } from '@/components/Post';
import './KlyxFeed.scss';

const KlyxFeed: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const activeFeedTab = useAppSelector(selectActiveFeedTab);
  const posts = useAppSelector(selectActiveFeed);
  const hasMore = useAppSelector(selectFeedHasMore);
  const isLoading = useAppSelector(selectFeedLoading);
  const locationRadius = useAppSelector((state) => state.dashboard.locationRadius);
  const locationUnit = useAppSelector((state) => state.dashboard.locationUnit);
  const userLocationName = useAppSelector((state) => state.dashboard.userLocationName);

  const [showRadiusSelector, setShowRadiusSelector] = useState(false);
  const observerTarget = useRef<HTMLDivElement>(null);

  const radiusPresets = [1, 5, 10, 50, 100, 250];

  // Fetch Klyque (following list) once on mount for all PostCards to use
  useEffect(() => {
    dispatch(fetchKlyque({ limit: 1000 }));
  }, [dispatch]);

  // Refresh feed when returning from create-post page
  useEffect(() => {
    if (!location.state?.postCreated) return;
    dispatch(resetFeedOffset());
    switch (activeFeedTab) {
      case 'spotlight':
        dispatch(fetchSpotlightFeed({ limit: 20, offset: 0, append: false }));
        break;
      case 'location':
        dispatch(fetchLocationFeed({ limit: 20, offset: 0, append: false, radius: locationRadius, unit: locationUnit }));
        break;
      case 'klyque':
        dispatch(fetchKlyqueFeed({ limit: 20, offset: 0, append: false }));
        break;
    }
    // Clear the state so a page refresh doesn't re-trigger
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state?.postCreated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Detect user's locale for unit preference on mount (only once)
  const hasInitialized = useRef(false);
  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const locale = navigator.language;
    // Use km for most countries except US, UK, and a few others
    const useMiles = ['en-US', 'en-GB', 'en-LR', 'en-MM'].some(l => locale.startsWith(l));
    if ((useMiles && locationUnit === 'km') || (!useMiles && locationUnit === 'miles')) {
      dispatch(setLocationUnit(useMiles ? 'miles' : 'km'));
    }
  }, [dispatch, locationUnit]);

  // Track last fetch params to prevent duplicate fetches
  const lastFetchRef = useRef<{ tab: string; radius: number; unit: string } | null>(null);

  // Fetch feed when tab changes or location settings change
  useEffect(() => {
    // Prevent duplicate fetches with same parameters
    const currentParams = {
      tab: activeFeedTab,
      radius: locationRadius,
      unit: locationUnit,
    };

    if (
      lastFetchRef.current &&
      lastFetchRef.current.tab === currentParams.tab &&
      lastFetchRef.current.radius === currentParams.radius &&
      lastFetchRef.current.unit === currentParams.unit
    ) {
      return; // Skip fetch if params haven't changed
    }

    lastFetchRef.current = currentParams;
    dispatch(resetFeedOffset());

    switch (activeFeedTab) {
      case 'spotlight':
        dispatch(fetchSpotlightFeed({ limit: 20, offset: 0, append: false }));
        break;
      case 'location':
        dispatch(fetchLocationFeed({ limit: 20, offset: 0, append: false, radius: locationRadius, unit: locationUnit }));
        break;
      case 'klyque':
        dispatch(fetchKlyqueFeed({ limit: 20, offset: 0, append: false }));
        break;
    }
  }, [activeFeedTab, locationRadius, locationUnit, dispatch]);

  // Infinite scroll
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [target] = entries;
      if (target.isIntersecting && hasMore && !isLoading) {
        dispatch(loadMoreFeed());
      }
    },
    [hasMore, isLoading, dispatch]
  );

  useEffect(() => {
    const element = observerTarget.current;
    if (!element) return;

    const observer = new IntersectionObserver(handleObserver, {
      threshold: 0.5,
    });

    observer.observe(element);

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [handleObserver]);

  const handleTabChange = (tab: 'spotlight' | 'location' | 'klyque') => {
    console.log('[KlyxFeed] Tab change requested:', tab);
    dispatch(setActiveFeedTab(tab));
  };

  const handleRadiusChange = (newRadius: number) => {
    dispatch(setLocationRadius(newRadius));
    setShowRadiusSelector(false);
  };

  const handleUnitToggle = () => {
    dispatch(setLocationUnit(locationUnit === 'miles' ? 'km' : 'miles'));
  };

  return (
    <section className="klyxfeed-section">
      <h2>KlyxFeed</h2>

      {/* Tabs */}
      <div className="feed-tabs">
        <button
          className={`feed-tab ${activeFeedTab === 'spotlight' ? 'active' : ''}`}
          onClick={() => handleTabChange('spotlight')}
        >
          ⭐ Spotlight
        </button>
        <button
          className={`feed-tab ${activeFeedTab === 'location' ? 'active' : ''}`}
          onClick={() => handleTabChange('location')}
        >
          📍 Near You
        </button>
        <button
          className={`feed-tab ${activeFeedTab === 'klyque' ? 'active' : ''}`}
          onClick={() => handleTabChange('klyque')}
        >
          👥 Klyque
        </button>
      </div>

      {/* Location Controls - Only show on Near You tab */}
      {activeFeedTab === 'location' && (
        <div className="location-controls">
          <div className="location-info">
            <span className="location-name">
              {userLocationName || 'Your Location'}
            </span>
            <div className="radius-control">
              <button
                className="radius-button"
                onClick={() => setShowRadiusSelector(!showRadiusSelector)}
              >
                {locationRadius} {locationUnit}
              </button>
              <button
                className="unit-toggle"
                onClick={handleUnitToggle}
                title={`Switch to ${locationUnit === 'miles' ? 'kilometers' : 'miles'}`}
              >
                {locationUnit === 'miles' ? 'mi' : 'km'}
              </button>
            </div>
          </div>

          {showRadiusSelector && (
            <div className="radius-selector">
              {radiusPresets.map((preset) => (
                <button
                  key={preset}
                  className={`radius-option ${locationRadius === preset ? 'active' : ''}`}
                  onClick={() => handleRadiusChange(preset)}
                >
                  {preset}
                </button>
              ))}
            </div>
          )}
        </div>
      )}


      {/* Feed Content */}
      <div className="feed-content">
        {isLoading && posts.length === 0 ? (
          <LoadingSpinner size="medium" message="Loading feed..." />
        ) : posts.length === 0 ? (
          <div className="feed-empty">
            <p>No posts to show</p>
            {activeFeedTab === 'location' && (
              <p className="hint">Set your location to see posts from your area</p>
            )}
            {activeFeedTab === 'klyque' && (
              <p className="hint">Follow users to see their posts here</p>
            )}
          </div>
        ) : (
          <>
            <div className="feed-posts">
              {posts.map((post) => (
                <PostCard key={post.id} post={post} />
              ))}
            </div>

            {/* Infinite scroll trigger */}
            {hasMore && (
              <div ref={observerTarget} className="feed-observer">
                {isLoading && <LoadingSpinner size="small" message="Loading more..." />}
              </div>
            )}

            {!hasMore && posts.length > 0 && (
              <div className="feed-end">You've reached the end</div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default KlyxFeed;
